import { Injectable } from '@angular/core';

interface TallySubmissionPayload {
  formId: string;
  respondentId: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    Tally?: { loadEmbeds: () => void };
  }
}

const TALLY_EMBED_SCRIPT_SRC = 'https://tally.so/widgets/embed.js';

@Injectable({ providedIn: 'root' })
export class TallySurveyService {
  private scriptLoaded?: Promise<void>;

  loadEmbeds(): void {
    this.loadScript().then(() => window.Tally?.loadEmbeds());
  }

  /** Works for both popups and iframe embeds — Tally posts this message to the parent window. */
  listenForSubmission(formId: string, onSubmitted: (payload: TallySubmissionPayload) => void): () => void {
    const listener = (event: MessageEvent) => {
      if (typeof event.data !== 'string' || !event.data.includes('Tally.FormSubmitted')) {
        return;
      }

      const { payload } = JSON.parse(event.data) as { payload: TallySubmissionPayload };
      if (payload.formId === formId) {
        onSubmitted(payload);
      }
    };

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }

  private loadScript(): Promise<void> {
    if (window.Tally) {
      return Promise.resolve();
    }

    if (!this.scriptLoaded) {
      this.scriptLoaded = new Promise(resolve => {
        const script = document.createElement('script');
        script.src = TALLY_EMBED_SCRIPT_SRC;
        script.async = true;
        script.addEventListener('load', () => resolve(), { once: true });
        document.head.appendChild(script);
      });
    }

    return this.scriptLoaded;
  }
}
